import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 0 })}`;
const fmtDate = (iso) => { const s = (iso || "").slice(0, 10); if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return ""; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };
const STAGE = {
  cobrado: { label: "Cobrado", dot: "bg-emerald-500", cls: "bg-emerald-500/10 text-emerald-600" },
  facturado: { label: "Facturado, sin pagar", dot: "bg-gold", cls: "bg-gold/15 text-gold" },
  por_facturar: { label: "Por facturar", dot: "bg-navy/30", cls: "bg-navy/10 text-navy/60" },
};

// Fecha con edición al hacer clic: muestra DD/MM/YYYY y al tocar abre el selector.
function DateCell({ label, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const shown = fmtDate(value);
  if (editing) {
    return (
      <label className="flex items-center justify-between gap-1.5">
        <span className="text-navy/40 shrink-0">{label}:</span>
        <input
          data-testid={testid}
          type="date"
          autoFocus
          defaultValue={(value || "").slice(0, 10)}
          onBlur={() => setEditing(false)}
          onChange={(e) => { onSave(e.target.value); setEditing(false); }}
          className="text-[11px] text-navy/80 border border-violet rounded px-1 py-0.5 outline-none w-[120px]"
        />
      </label>
    );
  }
  return (
    <button data-testid={testid} onClick={() => setEditing(true)} className="flex items-center justify-between gap-1.5 w-full group/date">
      <span className="text-navy/40 shrink-0">{label}:</span>
      <span className={`${shown ? "text-navy/70" : "text-violet/70 italic"} border border-transparent group-hover/date:border-navy/15 rounded px-1 py-0.5`}>
        {shown || "+ agregar"}
      </span>
    </button>
  );
}

export default function StudioFinancePipeline({ studio }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(`/studio/${studio}/finance/pipeline`).then((r) => setData(r.data)).catch(() => {});
  }, [studio]);
  useEffect(() => { load(); }, [load]);

  const patchDate = async (row, field, value) => {
    // reflejo optimista
    setData((d) => d && ({ ...d, rows: d.rows.map((r) => (r.id === row.id ? { ...r, [field]: value } : r)) }));
    try {
      const patch = { [field]: value };
      if (field === "paid_date" && value) patch.status = "paid";
      await api.patch(`/studio/${studio}/finance/entries/${row.id}`, patch);
      toast.success("Fecha guardada");
      load();
    } catch (e) {
      toast.error("No se pudo guardar la fecha");
      load();
    }
  };

  if (!data) return null;
  const { rows, stages } = data;

  return (
    <div className="space-y-4" data-testid="studio-finance-pipeline">
      <div className="flex items-baseline justify-between">
        <h3 className="font-editorial text-2xl tracking-tight">Pipeline financiero</h3>
        <span className="text-xs text-navy/40">{rows.length} conceptos · toca una fecha para editarla</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["cobrado", "facturado", "por_facturar"].map((k) => (
          <div key={k} data-testid={`pipeline-stage-${k}`} className="bg-white border border-navy/10 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${STAGE[k].dot}`} />
              <span className="overline text-navy/40 text-[0.6rem]">{STAGE[k].label}</span>
            </div>
            <div className="mt-2 text-xl font-medium">{money(stages[k].amount)}</div>
            <div className="text-xs text-navy/40">{stages[k].count} concepto(s)</div>
          </div>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="text-navy/40 text-sm py-8 text-center border border-dashed border-navy/15 rounded-lg">
          Aún no hay cotizaciones ni facturas registradas en los proyectos.
        </div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto">
          <div className="grid grid-cols-[1.3fr_1fr_110px_150px_190px_190px] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10 min-w-[940px]">
            <span>Proyecto</span><span>Concepto</span><span>Monto</span><span>Etapa</span><span>Fechas factura/cobro</span><span>Estimadas</span>
          </div>
          {rows.map((r) => (
            <div key={r.id} data-testid={`pipeline-row-${r.id}`} className="grid grid-cols-[1.3fr_1fr_110px_150px_190px_190px] gap-3 px-5 py-3 items-center border-b border-navy/5 last:border-0 text-sm min-w-[940px]">
              <div className="min-w-0"><div className="font-medium truncate">{r.project_name}</div><div className="text-[11px] text-navy/40">{r.project_status || ""}</div></div>
              <div className="min-w-0 truncate text-navy/70">{r.concept || "—"}</div>
              <div className="font-medium">{money(r.amount)}</div>
              <div><span className={`text-[11px] px-2 py-1 rounded-full ${STAGE[r.stage].cls}`}>{STAGE[r.stage].label}</span></div>
              <div className="text-[11px] space-y-0.5">
                <DateCell label="Fact" testid={`pipeline-fact-${r.id}`} value={r.invoice_date} onSave={(v) => patchDate(r, "date", v)} />
                <DateCell label="Cobro" testid={`pipeline-cobro-${r.id}`} value={r.paid_date} onSave={(v) => patchDate(r, "paid_date", v)} />
              </div>
              <div className="text-[11px] space-y-0.5">
                <DateCell label="Facturar" testid={`pipeline-facturar-${r.id}`} value={r.expected_invoice_date} onSave={(v) => patchDate(r, "expected_invoice_date", v)} />
                <DateCell label="Cobrar" testid={`pipeline-cobrar-${r.id}`} value={r.expected_payment_date} onSave={(v) => patchDate(r, "expected_payment_date", v)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
